import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import merge from '../helpers/merge';
import componentTranslations from './translations';
class I18nError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'I18nError';
        this.code = code;
        // restore prototype chain
        const actualProto = new.target.prototype;
        if (Object.setPrototypeOf) {
            Object.setPrototypeOf(this, actualProto);
        }
    }
}
// IMPORTANT: This requires non-inlined `process.env` values!
function isProductionEnvironment() {
    const { REACT_APP_ENVIRONMENT } = process.env;
    return (REACT_APP_ENVIRONMENT === 'live' ||
        REACT_APP_ENVIRONMENT === 'prod' ||
        REACT_APP_ENVIRONMENT === 'production');
}
/**
 * Note: These 2 error handles defines the default behavior for
 * translation issues. They rely on the presence of the
 * REACT_APP_ENVIRONMENT environment variable.
 *
 * Depending on the project, an additional callback can be passed to this
 * function. This allows to send a Sentry message e.g. without requiring
 * the i18n provider to know about the project-specific Sentry
 * configuration.
 */
function missingInterpolationHandler(callback, isProduction) {
    return (key) => {
        if (callback) {
            callback(key);
        }
        // Must be lazily initialized to allow this check to react
        // dynamically to changes made to `process.env`. Our tests rely
        // on this.
        if (!isProduction) {
            throw new I18nError(`Missing variable(s) for interpolation in key '${key}'`, 'i18n-failed');
        }
    };
}
function missingKeyHandler(callback, isProduction) {
    return (language, ns, key, fallbackValue) => {
        if (typeof callback === 'function') {
            callback(language, ns, key, fallbackValue);
        }
        // Must be lazily initialized to allow this check to react
        // dynamically to changes made to `process.env`. Our tests rely
        // on this.
        if (!isProduction) {
            throw new I18nError(`Missing translation key for language '${language}': '${ns}:${key}' (Namespace:Key). Fallback value: '${fallbackValue}'`, 'i18n-failed');
        }
    };
}
export function createI18nConfig(
// eslint-disable-next-line @typescript-eslint/ban-types
translations, onMissingKey, onMissingInterpolationVariable, isProduction = isProductionEnvironment(), overwriteOptions) {
    const resources = merge(componentTranslations, translations);
    const defaultOptions = {
        missingInterpolationHandler: missingInterpolationHandler(onMissingInterpolationVariable, isProduction),
        missingKeyHandler: missingKeyHandler(onMissingKey, isProduction),
        saveMissing: true,
        // Logging
        debug: process.env.DEBUG === 'true',
        // Namespaces (I18next's grouping mechanism) to load
        ns: 'default',
        defaultNS: 'default',
        supportedLngs: Object.keys(resources),
        // Fallback language
        fallbackLng: 'en-GB',
        interpolation: {
            escapeValue: false, // React escapes values already
        },
        // Merge the translation keys of the reusable components from this library with the passed translations. This allows to reuse this configuration while still allowing to use application-dependent translation keys
        resources,
        // Configuration for LanguageDetector
        detection: {
            order: ['querystring', 'localStorage', 'navigator'],
            // Keys/Params to lookup language from
            lookupQuerystring: 'lang',
            lookupLocalStorage: 'lang',
            // Where to cache
            caches: ['localStorage'],
        },
        // Configuration for initReactI18next
        react: {
            bindI18n: 'languageChanged loaded',
            bindStore: 'added removed', // Trigger rerender on these events
        },
    };
    // Load plugins and initialize i18next
    i18next
        .use(LanguageDetector)
        .use(initReactI18next)
        .init(Object.assign(Object.assign({}, defaultOptions), overwriteOptions));
    // i18n can't be returned directly (would return a Promise not a reference to this object)
    return i18next;
}
export default i18next;
